import { useState } from "react";
import axios from "axios";
import { useLang } from "@/lib/LanguageContext";
import { contactImage, studioContact } from "@/lib/portfolio";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const initial = {
    name: "",
    email: "",
    phone: "",
    project_type: "",
    wall_size: "",
    message: "",
};

export default function Contact() {
    const { t, lang } = useLang();
    const [form, setForm] = useState(initial);
    const [status, setStatus] = useState({ state: "idle", message: "" });

    const onChange = (e) => {
        setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        setStatus({ state: "loading", message: "" });
        try {
            await axios.post(`${API}/contact`, {
                ...form,
                language: lang,
            });
            setStatus({ state: "success", message: t.contact.form.success });
            setForm(initial);
        } catch (err) {
            console.error(err);
            setStatus({ state: "error", message: t.contact.form.error });
        }
    };

    return (
        <section
            id="contact"
            data-testid="contact-section"
            className="py-20 lg:py-32"
        >
            <div className="mx-auto max-w-screen-2xl px-6 md:px-12 lg:px-20">
                <div className="grid grid-cols-12 gap-8 md:gap-16">
                    {/* Left column */}
                    <div className="col-span-12 md:col-span-5">
                        <p className="overline text-terracotta mb-6">
                            {t.contact.eyebrow}
                        </p>
                        <h2 className="font-serif text-5xl lg:text-7xl font-light leading-[1.02] tracking-tight text-navy">
                            {t.contact.title}{" "}
                            <span className="italic text-terracotta">
                                {t.contact.titleItalic}
                            </span>
                        </h2>
                        <p className="mt-8 max-w-md text-navy/65 leading-relaxed">
                            {t.contact.cta}
                        </p>

                        <div className="mt-12 img-hover hidden md:block">
                            <img
                                src={contactImage}
                                alt="Painted wall detail"
                                className="w-full h-auto"
                                loading="lazy"
                            />
                        </div>

                        <div className="mt-12 space-y-4">
                            <a
                                href={studioContact.whatsappLink}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="contact-whatsapp"
                                className="flex items-center justify-between border-b border-hairline pb-4 group"
                            >
                                <span className="overline text-navy/55">
                                    {t.contact.direct.whatsapp}
                                </span>
                                <span className="font-serif text-xl text-navy group-hover:text-terracotta transition-colors">
                                    {studioContact.whatsapp} →
                                </span>
                            </a>
                            <a
                                href={studioContact.instagramLink}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="contact-instagram"
                                className="flex items-center justify-between border-b border-hairline pb-4 group"
                            >
                                <span className="overline text-navy/55">
                                    {t.contact.direct.instagram}
                                </span>
                                <span className="font-serif text-xl text-navy group-hover:text-terracotta transition-colors">
                                    {studioContact.instagram} →
                                </span>
                            </a>
                            <a
                                href={`mailto:${studioContact.email}`}
                                data-testid="contact-email"
                                className="flex items-center justify-between border-b border-hairline pb-4 group"
                            >
                                <span className="overline text-navy/55">
                                    {t.contact.direct.email}
                                </span>
                                <span className="font-serif text-xl text-navy group-hover:text-terracotta transition-colors">
                                    {studioContact.email} →
                                </span>
                            </a>
                        </div>
                    </div>

                    {/* Form */}
                    <div className="col-span-12 md:col-span-7 md:pl-8">
                        <form
                            onSubmit={onSubmit}
                            data-testid="contact-form"
                            className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-8"
                        >
                            <div className="md:col-span-1">
                                <label
                                    htmlFor="c-name"
                                    className="field-label"
                                >
                                    {t.contact.form.name} *
                                </label>
                                <input
                                    id="c-name"
                                    name="name"
                                    required
                                    value={form.name}
                                    onChange={onChange}
                                    data-testid="contact-input-name"
                                    className="field"
                                    autoComplete="name"
                                />
                            </div>
                            <div className="md:col-span-1">
                                <label
                                    htmlFor="c-email"
                                    className="field-label"
                                >
                                    {t.contact.form.email} *
                                </label>
                                <input
                                    id="c-email"
                                    type="email"
                                    name="email"
                                    required
                                    value={form.email}
                                    onChange={onChange}
                                    data-testid="contact-input-email"
                                    className="field"
                                    autoComplete="email"
                                />
                            </div>
                            <div className="md:col-span-1">
                                <label
                                    htmlFor="c-phone"
                                    className="field-label"
                                >
                                    {t.contact.form.phone}
                                </label>
                                <input
                                    id="c-phone"
                                    name="phone"
                                    value={form.phone}
                                    onChange={onChange}
                                    data-testid="contact-input-phone"
                                    className="field"
                                    autoComplete="tel"
                                />
                            </div>
                            <div className="md:col-span-1">
                                <label
                                    htmlFor="c-project"
                                    className="field-label"
                                >
                                    {t.contact.form.project}
                                </label>
                                <select
                                    id="c-project"
                                    name="project_type"
                                    value={form.project_type}
                                    onChange={onChange}
                                    data-testid="contact-input-project"
                                    className="field bg-transparent"
                                >
                                    <option value="">—</option>
                                    {t.contact.form.projects.map((p) => (
                                        <option key={p} value={p}>
                                            {p}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="md:col-span-2">
                                <label
                                    htmlFor="c-wall"
                                    className="field-label"
                                >
                                    {t.contact.form.wall}
                                </label>
                                <input
                                    id="c-wall"
                                    name="wall_size"
                                    value={form.wall_size}
                                    onChange={onChange}
                                    data-testid="contact-input-wall"
                                    className="field"
                                />
                            </div>
                            <div className="md:col-span-2">
                                <label
                                    htmlFor="c-message"
                                    className="field-label"
                                >
                                    {t.contact.form.message} *
                                </label>
                                <textarea
                                    id="c-message"
                                    name="message"
                                    required
                                    rows={5}
                                    value={form.message}
                                    onChange={onChange}
                                    data-testid="contact-input-message"
                                    className="field resize-none"
                                />
                            </div>

                            <div className="md:col-span-2 flex flex-col sm:flex-row sm:items-center justify-between gap-6 pt-4">
                                <button
                                    type="submit"
                                    disabled={status.state === "loading"}
                                    data-testid="contact-submit"
                                    className="btn-primary disabled:opacity-60"
                                >
                                    {status.state === "loading"
                                        ? t.contact.form.sending
                                        : t.contact.form.submit}{" "}
                                    →
                                </button>
                                {status.state === "success" && (
                                    <p
                                        data-testid="contact-success"
                                        className="text-sm text-teal max-w-sm"
                                    >
                                        {status.message}
                                    </p>
                                )}
                                {status.state === "error" && (
                                    <p
                                        data-testid="contact-error"
                                        className="text-sm text-terracotta max-w-sm"
                                    >
                                        {status.message}
                                    </p>
                                )}
                            </div>
                        </form>

                        <div className="md:hidden img-hover mt-12">
                            <img
                                src={contactImage}
                                alt="Painted wall detail"
                                className="w-full h-auto"
                                loading="lazy"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
